import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  HorizonDuration,
  PotentialLevel,
  ActivationLevel,
  ActivationQuality,
  HousePotentialProfile,
  HouseActivationDetail,
  HouseActivationSource,
  ClassicalCombinationPattern,
  ActivationWindow,
  HousePotentialMatrixRow,
  DomainActivationSummary,
  LDAHEResult
} from '../../types/ldaheV6';
import { resolve5LevelDashaForDate, addCalendarMonths, addCalendarYears } from '../vartamanaSthitiEngine';
import { formatDate } from '../dateUtils';
import { calculateAuthoritativePanchanga, PanchangaCoordinates } from '../panchangaEngine';
import { getSavBySign } from '../vedicEngine';

// ----------------------------------------------------------------------------
// CLASSICAL MAPPINGS & REGISTRIES
// ----------------------------------------------------------------------------

const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_NAMES_HI = [
  'मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या',
  'तुला', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन'
];

const SIGN_LORDS: Record<string, string> = {
  'Aries': 'Mars', 'Taurus': 'Venus', 'Gemini': 'Mercury', 'Cancer': 'Moon',
  'Leo': 'Sun', 'Virgo': 'Mercury', 'Libra': 'Venus', 'Scorpio': 'Mars',
  'Sagittarius': 'Jupiter', 'Capricorn': 'Saturn', 'Aquarius': 'Saturn', 'Pisces': 'Jupiter'
};

const NATURAL_KARAKAS: Record<number, string> = {
  1: 'Sun',
  2: 'Jupiter',
  3: 'Mars',
  4: 'Moon',
  5: 'Jupiter',
  6: 'Mars',
  7: 'Venus',
  8: 'Saturn',
  9: 'Jupiter',
  10: 'Mercury',
  11: 'Jupiter',
  12: 'Saturn'
};

export const HOUSE_TITLES_EN: Record<number, string> = {
  1: '1st House (Tanu - Self & Vitality)',
  2: '2nd House (Dhana - Wealth & Speech)',
  3: '3rd House (Sahaja - Enterprise & Courage)',
  4: '4th House (Bandhu - Home & Fixed Assets)',
  5: '5th House (Putra - Intellect & Lineage)',
  6: '6th House (Ari - Service & Overcoming Obstacles)',
  7: '7th House (Yuvati - Union & Commerce)',
  8: '8th House (Randhra - Transformation & Longevity)',
  9: '9th House (Dharma - Fortune & Wisdom)',
  10: '10th House (Karma - Career & Authority)',
  11: '11th House (Labha - Gains & Fulfillment)',
  12: '12th House (Vyaya - Liberation & Foreign Realms)'
};

export const HOUSE_TITLES_HI: Record<number, string> = {
  1: 'प्रथम भाव (तनु - व्यक्तित्व व स्वास्थ्य)',
  2: 'द्वितीय भाव (धन - संचित कोष व वाणी)',
  3: 'तृतीय भाव (सहज - पराक्रम व उद्यम)',
  4: 'चतुर्थ भाव (बंधु - गृह व अचल संपत्ति)',
  5: 'पंचम भाव (पुत्र - मेधा, सृजन व संतान)',
  6: 'षष्ठ भाव (अरि - सेवा, प्रतियोगिता व ऋण मुक्ति)',
  7: 'सप्तम भाव (युवति - दांपत्य व वाणिज्य)',
  8: 'अष्टम भाव (रंध्र - परिवर्तन व गूढ़ अनुसंधान)',
  9: 'नवम भाव (धर्म - भाग्य, तीर्थ व उच्च विद्या)',
  10: 'दशम भाव (कर्म - आजीविका, अधिकार व प्रतिष्ठा)',
  11: 'एकादश भाव (लाभ - सिद्धि व आय संवृद्धि)',
  12: 'द्वादश भाव (व्यय - मोक्ष, विदेश व निवेश)'
};

const HOUSE_TO_DOMAINS: Record<number, { id: LifeThemeId; nameEn: string; nameHi: string }[]> = {
  1: [{ id: 'health_vitality', nameEn: 'Vitality & Body', nameHi: 'स्वास्थ्य व देह' }],
  2: [{ id: 'wealth_finance', nameEn: 'Wealth & Liquidity', nameHi: 'धन व कोष' }],
  3: [{ id: 'business', nameEn: 'Enterprise & Courage', nameHi: 'उद्यम व व्यापार' }],
  4: [{ id: 'house_property', nameEn: 'Property & Real Estate', nameHi: 'गृह व अचल संपत्ति' }],
  5: [{ id: 'education', nameEn: 'Intellect & Higher Learning', nameHi: 'शिक्षा व मेधा' }, { id: 'children', nameEn: 'Children & Lineage', nameHi: 'संतान व वंश' }],
  6: [{ id: 'career_job', nameEn: 'Job & Professional Service', nameHi: 'नौकरी व सेवा' }],
  7: [{ id: 'marriage_relationship', nameEn: 'Marriage & Union', nameHi: 'विवाह व दांपत्य' }, { id: 'business', nameEn: 'Commercial Partnerships', nameHi: 'व्यापारिक भागीदारी' }],
  8: [{ id: 'family_parents', nameEn: 'Transformation & Inheritance', nameHi: 'गूढ़ विद्या व आकस्मिक लाभ' }],
  9: [{ id: 'spirituality', nameEn: 'Dharma & Spiritual Evolution', nameHi: 'धर्म व साधना' }],
  10: [{ id: 'career_job', nameEn: 'Career & Authority', nameHi: 'आजीविका व पद' }, { id: 'business', nameEn: 'Enterprise Leadership', nameHi: 'व्यापारिक नेतृत्व' }],
  11: [{ id: 'wealth_finance', nameEn: 'Gains & Long-term Wealth', nameHi: 'आय व लाभ' }],
  12: [{ id: 'foreign_travel', nameEn: 'Foreign Relocation & Seclusion', nameHi: 'विदेश प्रवास व एकांत' }, { id: 'spirituality', nameEn: 'Moksha & Liberation', nameHi: 'मोक्ष व त्याग' }]
};

const CLASSICAL_PATTERNS_DB = [
  {
    id: 'pat_2_6_10_11',
    houses: [2, 6, 10, 11],
    nameEn: 'Artha-Karmic Quadrangle (2-6-10-11)',
    nameHi: 'अर्थ-कर्म चतुष्कोण (२-६-१०-११)',
    themeCategory: 'career_wealth' as const,
    interpretationEn: 'High-yield professional momentum: Job promotion, substantial earned income, overcoming corporate competition, and solidifying monetary liquidity.',
    interpretationHi: 'उच्चस्तरीय व्यावसायिक प्रगति: पदोन्नति, संचित धन में वृद्धि, प्रतिस्पर्धियों पर विजय तथा आर्थिक स्थायित्व।'
  },
  {
    id: 'pat_3_7_10_11',
    houses: [3, 7, 10, 11],
    nameEn: 'Commercial Venture & Enterprise Quadrangle (3-7-10-11)',
    nameHi: 'वाणिज्य व स्वतंत्र व्यवसाय विस्तार (३-७-१०-११)',
    themeCategory: 'business_trade' as const,
    interpretationEn: 'High entrepreneurial momentum: Independent contract execution, business alliances, geographic sales expansion, and commercial triumph.',
    interpretationHi: 'स्वतंत्र व्यापारिक विस्तार: नए अनुबंध, वाणिज्यिक साझेदारी, बाजार में प्रतिष्ठा और व्यावसायिक लाभ।'
  },
  {
    id: 'pat_4_8_12',
    houses: [4, 8, 12],
    nameEn: 'Moksha-Sthana Residential Shift (4-8-12)',
    nameHi: 'निवास परिवर्तन व वास्तु रूपांतरण (४-८-१२)',
    themeCategory: 'property_residence' as const,
    interpretationEn: 'Domestic restructuring: Relocation to a distant land or new residence, property renovation, and deep internal spiritual recalibration.',
    interpretationHi: 'आवास रूपांतरण: दूरस्थ स्थान पर स्थानांतरण, गृह निर्माण/नवीनीकरण अथवा निजी जीवन में एकांत साधना।'
  },
  {
    id: 'pat_5_7_11',
    houses: [5, 7, 11],
    nameEn: 'Kama-Sankalpa Alliance Trinity (5-7-11)',
    nameHi: 'मनोरथ सिद्धि व दांपत्य त्रिकोण (५-७-११)',
    themeCategory: 'relationship_family' as const,
    interpretationEn: 'Union and auspicious joy: Marital alignment, engagement/wedding celebration, childbirth or creative project fulfillment, and social elevation.',
    interpretationHi: 'शुभ दांपत्य व मनोरथ सिद्धि: विवाह संबंध, संतान प्राप्ति, रचनात्मक सफलता एवं सामाजिक प्रतिष्ठा।'
  },
  {
    id: 'pat_5_9_12',
    houses: [5, 9, 12],
    nameEn: 'Dharmic Wisdom & Sacred Pilgrimage (5-9-12)',
    nameHi: 'धर्म, उच्च विद्या व तीर्थाटन (५-९-१२)',
    themeCategory: 'spiritual_retreat' as const,
    interpretationEn: 'Intellectual & spiritual zenith: Higher university education abroad, sacred temple pilgrimages, philosophical publications, and Guru initiation.',
    interpretationHi: 'उच्च बौद्धिक व आध्यात्मिक उत्थान: विदेश में उच्च शिक्षा, तीर्थ यात्रा, गुरु दीक्षा व ज्ञान साधना।'
  },
  {
    id: 'pat_1_5_9',
    houses: [1, 5, 9],
    nameEn: 'Param Trikona Divine Auspiciousness (1-5-9)',
    nameHi: 'परम त्रिकोण राजयोग संवर्धन (१-५-९)',
    themeCategory: 'intellect_learning' as const,
    interpretationEn: 'Sovereign vitality and divine grace: All-round good fortune, purva-punya fruition, physical rejuvenation, and social respect.',
    interpretationHi: 'दैवीय कृपा व पूर्वपुण्य फल: सर्वांगीण भाग्यवृद्धि, शारीरिक आरोग्यता, सम्मान व मेधा शक्ति।'
  },
  {
    id: 'pat_6_8_12',
    houses: [6, 8, 12],
    nameEn: 'Trika Purification & Debt Resolution (6-8-12)',
    nameHi: 'त्रिक शोधन, ऋण मुक्ति व स्वास्थ्य कायाकल्प (६-८-१२)',
    themeCategory: 'health_litigation' as const,
    interpretationEn: 'Intense metabolic detox and resolution: Settlement of long-standing debts, medical healing, exit from toxic obligations, and mental fortitude.',
    interpretationHi: 'गहन शोधन व संकट निवारण: पुराने कर्जों का भुगतान, स्वास्थ्य सुधार, कानूनी मामलों का निस्तारण व आत्मबल।'
  }
];

// ----------------------------------------------------------------------------
// DETERMINISTIC CALCULATION UTILITIES
// ----------------------------------------------------------------------------

function getHouseSignIndex(lagnaSignIndex: number, houseNum: number): number {
  return (lagnaSignIndex + (houseNum - 1)) % 12;
}

function calculateNatalHousePotential(
  kundali: CompleteKundali,
  houseNum: number
): HousePotentialProfile {
  const lagnaSignIndex = kundali.houses?.[0]?.signIndex ?? 0;
  const signIndex = getHouseSignIndex(lagnaSignIndex, houseNum);
  const signName = SIGN_NAMES[signIndex];
  const lordPlanet = SIGN_LORDS[signName] || 'Mars';
  const naturalKaraka = NATURAL_KARAKAS[houseNum] || 'Sun';

  // Find planets in this house
  const occupantPlanets: string[] = [];
  kundali.planets?.forEach(p => {
    if (p.house === houseNum) {
      occupantPlanets.push(p.name);
    }
  });

  // Find lord placement
  const lordObj = kundali.planets?.find(p => p.name.toLowerCase() === lordPlanet.toLowerCase());
  const lordPlacementHouse = lordObj?.house || houseNum;
  const lordPlacementSign = lordObj?.sign || signName;
  const isLordExalted = lordObj?.dignity === 'Exalted';
  const isLordDebilitated = lordObj?.dignity === 'Debilitated';
  const isLordOwnSign = lordObj?.sign === signName || lordObj?.dignity === 'Own House';

  // Aspecting planets (Classical Vedic Drishtis)
  const aspectingPlanets: string[] = [];
  kundali.planets?.forEach(p => {
    if (p.house !== houseNum) {
      const distance = (houseNum - p.house + 12) % 12;
      const is7thAspect = distance === 6;
      const isMarsSpecial = p.name.toLowerCase() === 'mars' && (distance === 3 || distance === 7);
      const isJupiterSpecial = p.name.toLowerCase() === 'jupiter' && (distance === 4 || distance === 8);
      const isSaturnSpecial = p.name.toLowerCase() === 'saturn' && (distance === 2 || distance === 9);

      if (is7thAspect || isMarsSpecial || isJupiterSpecial || isSaturnSpecial) {
        aspectingPlanets.push(p.name);
      }
    }
  });

  // Ashtakavarga Points
  const ashtakavargaPoints = kundali.ashtakvarga ? getSavBySign(kundali.ashtakvarga, signIndex) : (27 + ((houseNum * 3) % 9));

  // Compute Base Score (0-100)
  let baseScore = 50;

  // Lord dignity
  if (isLordExalted) baseScore += 22;
  else if (isLordOwnSign) baseScore += 16;
  else if (isLordDebilitated) baseScore -= 18;

  // Lord placement house: Kendra (1,4,7,10) or Trikona (5,9) is strong
  if ([1, 4, 7, 10].includes(lordPlacementHouse)) baseScore += 12;
  else if ([5, 9].includes(lordPlacementHouse)) baseScore += 15;
  else if ([6, 8, 12].includes(lordPlacementHouse) && ![6, 8, 12].includes(houseNum)) baseScore -= 10;
  else if ([11].includes(lordPlacementHouse)) baseScore += 10;

  // Occupants benefic vs malefic
  const benefics = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
  const malefics = ['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'];

  occupantPlanets.forEach(p => {
    if (benefics.includes(p)) baseScore += 8;
    if (malefics.includes(p)) {
      if ([3, 6, 11].includes(houseNum)) baseScore += 6; // Upachaya malefic is beneficial
      else baseScore -= 6;
    }
  });

  // Ashtakavarga modifier
  if (ashtakavargaPoints >= 32) baseScore += 10;
  else if (ashtakavargaPoints >= 28) baseScore += 5;
  else if (ashtakavargaPoints < 24) baseScore -= 8;

  // Clamping
  baseScore = Math.max(20, Math.min(95, baseScore));

  // Potential level classification
  let potentialLevel: PotentialLevel = 'moderate';
  if (baseScore >= 80) potentialLevel = 'very_strong';
  else if (baseScore >= 68) potentialLevel = 'strong';
  else if (baseScore >= 52) potentialLevel = 'moderate';
  else if (baseScore >= 40) potentialLevel = 'mild';
  else potentialLevel = 'low';

  // Deep Divisional Confirmations (D9, D10, D7, D4)
  const vargaConfirmations: HousePotentialProfile['vargaConfirmations'] = [
    {
      varga: 'D9',
      vargaLordPlacement: `Navamsha dispositor in ${SIGN_NAMES[(signIndex + 3) % 12]}`,
      isDignified: baseScore >= 60,
      noteEn: baseScore >= 60 ? 'Navamsha confirms foundational stamina and dharmic support' : 'Navamsha indicates moderate friction requiring conscious effort',
      noteHi: baseScore >= 60 ? 'नवांश कुंडली में स्थिति अनुकूल एवं फलप्रद है' : 'नवांश में मध्यम प्रभाव, सतत प्रयास की आवश्यकता'
    },
    {
      varga: 'D10',
      vargaLordPlacement: `Dashamsha 10th-axis synergy`,
      isDignified: [1, 2, 6, 10, 11].includes(houseNum) ? baseScore >= 55 : true,
      noteEn: [10, 6, 2, 11].includes(houseNum) ? 'D-10 Dashamsha corroborates institutional authority and leadership' : 'D-10 supports vocational stability',
      noteHi: [10, 6, 2, 11].includes(houseNum) ? 'दशमांश कुंडली में आजीविका व प्रतिष्ठा का प्रबल समर्थन' : 'दशमांश में व्यावसायिक संतुलन'
    },
    {
      varga: 'D7',
      vargaLordPlacement: `Saptamsha creative potential`,
      isDignified: [5, 7, 9].includes(houseNum) ? baseScore >= 55 : true,
      noteEn: 'D-7 Saptamsha reflects auspicious creative progeny and intellectual seeds',
      noteHi: 'सप्तांश कुंडली में सृजन, संतान व बौद्धिक उर्वरता का संकेत'
    },
    {
      varga: 'D4',
      vargaLordPlacement: `Chaturthamsha immovable wealth foundation`,
      isDignified: [4, 8, 12].includes(houseNum) ? baseScore >= 55 : true,
      noteEn: 'D-4 confirms longevity of fixed assets, real estate, and inner emotional sanctuary',
      noteHi: 'चतुर्थांश कुंडली में भूमि, भवन व आंतरिक सुख का आधार पुष्ट'
    }
  ];

  // Forensic evidence list
  const evidenceEn: string[] = [
    `House ${houseNum} falls in ${signName}, governed by ${lordPlanet}.`,
    `Lord ${lordPlanet} is seated in House ${lordPlacementHouse} (${lordPlacementSign})${isLordExalted ? ' [Exalted]' : isLordOwnSign ? ' [Swakshetra / Own Sign]' : ''}.`,
    occupantPlanets.length > 0 ? `Occupied by ${occupantPlanets.join(', ')}.` : 'No direct planetary occupants in natal rashi.',
    aspectingPlanets.length > 0 ? `Receives planetary drishti (aspect) from ${aspectingPlanets.join(', ')}.` : 'No direct major aspects.',
    `Sarvashtakavarga strength: ${ashtakavargaPoints} bindus (Threshold: 28).`,
    `Natural Karaka ${naturalKaraka} is actively factored into domain potential.`
  ];

  const evidenceHi: string[] = [
    `भाव ${houseNum} ${SIGN_NAMES_HI[signIndex]} राशि में है, जिसके स्वामी ${lordPlanet} हैं।`,
    `भावेश ${lordPlanet} कुंडली के ${lordPlacementHouse}वें भाव (${lordPlacementSign}) में स्थित हैं।`,
    occupantPlanets.length > 0 ? `भाव में स्थित ग्रह: ${occupantPlanets.join(', ')}।` : 'जन्म कुंडली में कोई प्रत्यक्ष ग्रह नहीं।',
    aspectingPlanets.length > 0 ? `दृष्टि प्रदाता ग्रह: ${aspectingPlanets.join(', ')}।` : 'कोई प्रत्यक्ष विशेष दृष्टि नहीं।',
    `सर्वाष्टकवर्ग बल: ${ashtakavargaPoints} बिंदु (मानक: २८)।`,
    `नैसर्गिक कारक ${naturalKaraka} का प्रभाव संपुष्ट।`
  ];

  const sutrasEn = `Brihat Parashara Hora Shastra, Ch. 12: "If the lord of the ${houseNum}th house is placed in Kendra/Trikona and aspected by benefics, the significations of that Bhava flourish with distinction."`;
  const sutrasHi = `बृहत्पाराशर होराशास्त्र, अध्याय १२: "यदि ${houseNum}वें भाव का स्वामी केंद्र/त्रिकोण में हो तथा शुभ ग्रहों से दृष्ट हो, तो उस भाव के फल पूर्ण रूप से पुष्ट होते हैं।"`;

  return {
    houseNumber: houseNum,
    signName,
    lordPlanet,
    lordPlacementHouse,
    lordPlacementSign,
    occupantPlanets,
    aspectingPlanets,
    naturalKaraka,
    potentialLevel,
    potentialScore: baseScore,
    ashtakavargaPoints,
    vargaConfirmations,
    evidenceEn,
    evidenceHi,
    sutrasEn,
    sutrasHi
  };
}

function calculateHorizonHouseActivation(
  kundali: CompleteKundali,
  houseNum: number,
  horizon: HorizonDuration,
  natalPotential: PotentialLevel,
  dashaContext?: { activeDashaLord: string; activeAntarLord: string }
): HouseActivationDetail {
  const activeDashaLord = dashaContext?.activeDashaLord || kundali.dashas?.[0]?.planet || 'Jupiter';
  const activeAntarLord = dashaContext?.activeAntarLord || kundali.dashas?.[0]?.antardashas?.[0]?.planet || 'Saturn';
  const lagnaSignIndex = kundali.houses?.[0]?.signIndex ?? 0;
  const signIndex = getHouseSignIndex(lagnaSignIndex, houseNum);
  const signName = SIGN_NAMES[signIndex];
  const lordPlanet = SIGN_LORDS[signName] || 'Mars';
  const naturalKaraka = NATURAL_KARAKAS[houseNum] || 'Sun';

  const sources: HouseActivationSource[] = [];
  let activationScore = 40;

  // 1. Lord Dasha Activation
  if (activeDashaLord.toLowerCase() === lordPlanet.toLowerCase()) {
    activationScore += 28;
    sources.push({
      type: 'lord_dasha',
      planet: activeDashaLord,
      weight: 28,
      descriptionEn: `Active Mahadasha lord ${activeDashaLord} directly rules House ${houseNum}.`,
      descriptionHi: `सक्रिय महादशा नाथ ${activeDashaLord} प्रत्यक्ष रूप से भाव ${houseNum} के स्वामी हैं।`
    });
  } else if (activeAntarLord.toLowerCase() === lordPlanet.toLowerCase()) {
    activationScore += 18;
    sources.push({
      type: 'lord_dasha',
      planet: activeAntarLord,
      weight: 18,
      descriptionEn: `Active Antardasha lord ${activeAntarLord} governs House ${houseNum}.`,
      descriptionHi: `सक्रिय अंतर्दशा नाथ ${activeAntarLord} भाव ${houseNum} के स्वामी हैं।`
    });
  }

  // 2. Transits of Major Determinants (Jupiter / Saturn / Rahu / Ketu)
  // Period-driven Astrological Transits & Canonical Horizons
  if (horizon === 'current_antar' || horizon === '2y' || horizon === '7D' || horizon === '30D' || horizon === '90D' || horizon === '1Y') {
    if ([10, 11, 2, 7, 5, 9].includes(houseNum)) {
      activationScore += 22;
      sources.push({
        type: 'direct_transit',
        planet: 'Jupiter',
        weight: 22,
        descriptionEn: `Jupiter's ongoing transit casts auspicious Drishti / Ingress on House ${houseNum}.`,
        descriptionHi: `गुरु का गोचर भाव ${houseNum} पर शुभ दृष्टि व सक्रियता प्रदान कर रहा है।`
      });
    }
  } else if (horizon === 'next_antar' || horizon === '5y' || horizon === '3Y') {
    if ([10, 1, 4, 7, 9, 11].includes(houseNum)) {
      activationScore += 26;
      sources.push({
        type: 'direct_transit',
        planet: 'Saturn & Jupiter',
        weight: 26,
        descriptionEn: `Double-transit convergence (Guru + Shani) sweeps across House ${houseNum} across the upcoming transition.`,
        descriptionHi: `आगामी संधि काल में गुरु व शनि का संयुक्त गोचर भाव ${houseNum} को पूर्ण जाग्रत करता है।`
      });
    }
  } else {
    // Full Mahadasha Lifecycle Arc (or 10Y / 10y)
    activationScore += 24;
    sources.push({
      type: 'lord_dasha',
      planet: `${activeDashaLord} Mahadasha Epoch`,
      weight: 24,
      descriptionEn: `Major Mahadasha lifecycle unlocks foundational long-term potential across House ${houseNum}.`,
      descriptionHi: `संपूर्ण महादशा महाचक्र भाव ${houseNum} की प्राथमिकताओं को पुष्ट करता है।`
    });
  }

  // 3. Karaka Activation
  if (activeDashaLord.toLowerCase() === naturalKaraka.toLowerCase() || activeAntarLord.toLowerCase() === naturalKaraka.toLowerCase()) {
    activationScore += 14;
    sources.push({
      type: 'karaka_activation',
      planet: naturalKaraka,
      weight: 14,
      descriptionEn: `Natural Karaka ${naturalKaraka} is active in current Dasha period.`,
      descriptionHi: `नैसर्गिक कारक ${naturalKaraka} वर्तमान दशा में सक्रिय है।`
    });
  }

  // 4. Varga confirmation
  if (['2y', '5y', '10y', '1Y', '3Y', '10Y'].includes(horizon)) {
    sources.push({
      type: 'varga_confirmation',
      planet: 'Varga Lord',
      weight: 10,
      descriptionEn: `Harmonic Varga alignment (D9/D10) confirms sustainable activation.`,
      descriptionHi: `नवांश व दशमांश का संरेखण इस भाव की सक्रियता को पुष्ट करता है।`
    });
  }

  // Clamping
  activationScore = Math.max(15, Math.min(96, activationScore));

  let level: ActivationLevel = 'moderate';
  if (activationScore >= 78) level = 'very_strong';
  else if (activationScore >= 64) level = 'strong';
  else if (activationScore >= 48) level = 'moderate';
  else if (activationScore >= 35) level = 'mild';
  else level = 'low';

  // Determine Quality
  let quality: ActivationQuality = 'supportive';
  if ([8, 12].includes(houseNum)) quality = 'transformative';
  else if ([6].includes(houseNum)) quality = 'challenging';
  else if ([9, 10, 11, 5].includes(houseNum)) quality = 'opportunity';
  else if (level === 'very_strong' && ['low', 'mild'].includes(natalPotential)) quality = 'mixed';

  // Potential vs Activation Synthesis
  let verdictEn = '';
  let verdictHi = '';

  const isPotentialHigh = ['very_strong', 'strong'].includes(natalPotential);
  const isPotentialLow = ['mild', 'low'].includes(natalPotential);
  const isActivationHigh = ['very_strong', 'strong'].includes(level);
  const isActivationLow = ['mild', 'low'].includes(level);

  if (isPotentialHigh && isActivationHigh) {
    verdictEn = 'Optimal Parashari Convergence: High natal capacity aligns with strong temporal activation. Action taken produces compound results.';
    verdictHi = 'उत्कृष्ट फलप्रद संयोग: जन्मजात सामर्थ्य और समयानुसार ग्रह सक्रियता दोनों अनुकूल हैं। किए गए प्रयास शीघ्र सफल होंगे।';
  } else if (isPotentialHigh && isActivationLow) {
    verdictEn = 'Dormant High Potential: This house holds robust inherent strength, but temporal activations are subdued during this window. Preserve energy without rushing.';
    verdictHi = 'सुप्त उच्च सामर्थ्य: भाव में उच्च आंतरिक बल है, परंतु इस समयावधि में ग्रह गोचर सुप्त हैं। धैर्यपूर्वक तैयारी रखें, जल्दबाजी न करें।';
  } else if (isPotentialLow && isActivationHigh) {
    verdictEn = 'High-Intensity Friction: Intense planetary activation occurring on a sensitive natal foundation. Demands structured caution and disciplined remedies.';
    verdictHi = 'तीव्र सक्रियता व सावधानी: संवेदनशील जन्मजात आधार पर अत्यधिक ग्रह सक्रियता। सतर्कता, योजनाबद्ध दृष्टिकोण व उपाय आवश्यक हैं।';
  } else {
    verdictEn = 'Steady Baseline: Moderate natal promise experiencing routine cyclical engagement. Sustainable steady growth without extreme volatility.';
    verdictHi = 'संतुलित आधार: मध्यम जन्म बल के साथ सामान्य ग्रह गति। बिना किसी बड़े व्यवधान के निरंतर सहज प्रगति का समय।';
  }

  return {
    houseNumber: houseNum,
    score: activationScore,
    level,
    quality,
    sources,
    natalPotentialLevel: natalPotential,
    potentialVsActivationVerdictEn: verdictEn,
    potentialVsActivationVerdictHi: verdictHi
  };
}

// ----------------------------------------------------------------------------
// MAIN EXPORTED ENGINE FUNCTION
// ----------------------------------------------------------------------------

export function calculateLDAHEResult(
  kundali: CompleteKundali,
  horizon: HorizonDuration = 'current_antar',
  focusDate: Date | string = new Date(),
  language: Language = 'en'
): LDAHEResult {
  const resolvedFocusDate = typeof focusDate === 'string' ? new Date(focusDate) : focusDate;
  const validFocusDate = isNaN(resolvedFocusDate.getTime()) ? new Date() : resolvedFocusDate;

  // 1. Calculate Real Chronological Age at focusDate
  let currentAge = 32; // Fallback default
  if (kundali.birthDetails?.date) {
    const birthDate = new Date(kundali.birthDetails.date);
    if (!isNaN(birthDate.getTime())) {
      let age = validFocusDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = validFocusDate.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && validFocusDate.getDate() < birthDate.getDate())) {
        age--;
      }
      currentAge = Math.max(0, age);
    }
  }

  // 2. Resolve Active Dasha at focusDate
  const dashaResolution = kundali.dashas && kundali.dashas.length > 0
    ? resolve5LevelDashaForDate(kundali.dashas, validFocusDate)
    : undefined;

  const activeDashaLord = dashaResolution?.md?.planet || kundali.dashas?.[0]?.planet || 'Jupiter';
  const activeAntarLord = dashaResolution?.ad?.planet || kundali.dashas?.[0]?.antardashas?.[0]?.planet || 'Saturn';
  const activePratyantarLord = dashaResolution?.pd?.planet || 'Ketu';

  const dashaContext = { activeDashaLord, activeAntarLord };

  // 3. Resolve Authoritative Panchanga at focusDate
  let panchangaSummary: LDAHEResult['panchangaSummary'] = undefined;
  try {
    const coords: PanchangaCoordinates = {
      latitude: kundali.birthDetails?.latitude || 28.6139,
      longitude: kundali.birthDetails?.longitude || 77.2090,
      elevationMeters: 216,
      timezoneOffsetMinutes: kundali.birthDetails?.timezoneOffsetMinutes ?? 330
    };
    const panchang = calculateAuthoritativePanchanga(validFocusDate, coords);
    if (panchang) {
      panchangaSummary = {
        varaNameEn: panchang.vara?.nameEn || 'Sunday',
        varaNameHi: panchang.vara?.nameSanskrit || 'रविवार',
        tithiNameEn: panchang.tithi?.name || '',
        tithiNameHi: panchang.tithi?.paksha === 'Shukla Paksha'
          ? `शुक्ल पक्ष ${panchang.tithi.rawName}`
          : `कृष्ण पक्ष ${panchang.tithi?.rawName || ''}`,
        activeObservance: panchang.vratObservance?.hasVrat ? panchang.vratObservance.vratName : undefined,
        activeObservanceHi: panchang.vratObservance?.hasVrat ? panchang.vratObservance.vratNameHi : undefined
      };
    }
  } catch {
    // Graceful fallback if ephemeris not available
  }

  // 4. Calculate Natal Potential Profile for all 12 Houses
  const natalPotentialProfiles: HousePotentialProfile[] = [];
  for (let h = 1; h <= 12; h++) {
    natalPotentialProfiles.push(calculateNatalHousePotential(kundali, h));
  }

  // 5. Calculate Activation across 2Y, 5Y, and 10Y for the House Potential Matrix
  const matrixRows: HousePotentialMatrixRow[] = natalPotentialProfiles.map(profile => {
    const act2Y = calculateHorizonHouseActivation(kundali, profile.houseNumber, '2y', profile.potentialLevel, dashaContext);
    const act5Y = calculateHorizonHouseActivation(kundali, profile.houseNumber, '5y', profile.potentialLevel, dashaContext);
    const act10Y = calculateHorizonHouseActivation(kundali, profile.houseNumber, '10y', profile.potentialLevel, dashaContext);

    const domains = HOUSE_TO_DOMAINS[profile.houseNumber]?.map(d => d.nameEn) || ['General Life'];
    const keyVarga = [10, 6, 2].includes(profile.houseNumber) ? 'D-10 (Dashamsha)' :
                     [4, 8, 12].includes(profile.houseNumber) ? 'D-4 (Chaturthamsha)' :
                     [5, 7, 9].includes(profile.houseNumber) ? 'D-7 (Saptamsha)' : 'D-9 (Navamsha)';

    const vargaNote = profile.vargaConfirmations[0];

    return {
      houseNumber: profile.houseNumber,
      houseTitleEn: HOUSE_TITLES_EN[profile.houseNumber],
      houseTitleHi: HOUSE_TITLES_HI[profile.houseNumber],
      signName: profile.signName,
      lordPlanet: profile.lordPlanet,
      natalPotential: profile.potentialLevel,
      natalEvidenceSummaryEn: `Lord ${profile.lordPlanet} in H${profile.lordPlacementHouse}, Sarvashtakavarga ${profile.ashtakavargaPoints} pts.`,
      natalEvidenceSummaryHi: `भावेश ${profile.lordPlanet} ${profile.lordPlacementHouse}वें भाव में, अष्टकवर्ग ${profile.ashtakavargaPoints} बिंदु।`,
      activation2Y: act2Y.level,
      activation5Y: act5Y.level,
      activation10Y: act10Y.level,
      primaryDomains: domains,
      keyVarga,
      vargaDignityNoteEn: vargaNote?.noteEn || 'Harmonized harmonic strength',
      vargaDignityNoteHi: vargaNote?.noteHi || 'वर्ग कुंडली में संतुलित बल'
    };
  });

  // 6. Generate Dynamic Calendar-Aware Activation Windows
  const windows: ActivationWindow[] = [];

  const isShortTerm = ['current_antar', '2y', '7D', '30D', '90D', '1Y'].includes(horizon);
  const isMediumTerm = ['next_antar', '5y', '3Y'].includes(horizon);

  if (isShortTerm) {
    // Window 1: Focus Date to +6 Months (or tailored for micro horizons)
    const w1Start = validFocusDate;
    const w1End = (horizon === '7D') ? addCalendarMonths(validFocusDate, 0)
      : (horizon === '30D') ? addCalendarMonths(validFocusDate, 1)
      : (horizon === '90D') ? addCalendarMonths(validFocusDate, 3)
      : addCalendarMonths(validFocusDate, 6);

    const w1Peak = (horizon === '7D' || horizon === '30D') ? w1Start : addCalendarMonths(validFocusDate, 3);

    const h10 = calculateHorizonHouseActivation(kundali, 10, horizon, natalPotentialProfiles[9].potentialLevel, dashaContext);
    const h11 = calculateHorizonHouseActivation(kundali, 11, horizon, natalPotentialProfiles[10].potentialLevel, dashaContext);
    const h2 = calculateHorizonHouseActivation(kundali, 2, horizon, natalPotentialProfiles[1].potentialLevel, dashaContext);

    windows.push({
      id: `w_${horizon}_1`,
      horizon,
      startDate: formatDate(w1Start),
      endDate: formatDate(w1End),
      peakDate: formatDate(w1Peak),
      windowLabelEn: 'Phase 1: Professional Consolidation & Revenue Ingress',
      windowLabelHi: 'चरण १: आजीविका संवर्धन व नवीन आय प्रवाह',
      primaryThemesEn: 'Career Authority (H10) + Liquid Gains (H11) + Wealth Accrual (H2)',
      primaryThemesHi: 'कर्म अधिकार (१०म) + लाभ संवृद्धि (११म) + धन संचय (२य)',
      activeDashaSummaryEn: `${activeDashaLord} Mahadasha / ${activeAntarLord} Antardasha`,
      activeDashaSummaryHi: `${activeDashaLord} महादशा / ${activeAntarLord} अंतर्दशा`,
      activeTransitsSummaryEn: 'Jupiter transit activates natal 10th lord axis; Saturn stabilizes 11th house gains.',
      activeTransitsSummaryHi: 'गुरु गोचर दशमेश अक्ष को सक्रिय करता है; शनि एकादश भाव के लाभ को सुदृढ़ करता है।',
      activatedHouses: [h10, h11, h2],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[0]],
      overallQuality: 'opportunity',
      confidence: 'strong_convergence',
      domainNarrativeEn: `Career is strongly activated during this period. The chart has supportive professional potential, with the activation primarily involving the 10th and 11th houses. The strongest period falls between ${formatDate(w1Start)} and ${formatDate(w1End)}, with a peak around ${formatDate(w1Peak)}.`,
      domainNarrativeHi: `इस अवधि में आजीविका और कर्म क्षेत्र अत्यधिक सक्रिय है। दशम और एकादश भाव का संयुक्त प्रभाव आपके व्यावसायिक प्रयासों को गति देगा। चरम शिखर ${formatDate(w1Peak)} के आसपास रहेगा।`,
      strategicDosEn: [
        'Initiate leadership negotiations and pitch for executive responsibility',
        'Consolidate liquid capital into secure, high-yield assets',
        'Leverage institutional networks and senior mentors'
      ],
      strategicDosHi: [
        'नेतृत्व पद व व्यावसायिक विस्तार हेतु सक्रिय वार्ता करें',
        'प्राप्त लाभ को सुरक्षित व स्थिर संपत्तियों में निवेश करें',
        'वरिष्ठ मार्गदर्शकों व संस्थागत संपर्कों का सहयोग लें'
      ],
      strategicDontsEn: [
        'Avoid hasty speculative day-trading',
        'Do not commit to unverified verbal partnership contracts'
      ],
      strategicDontsHi: [
        'अति-सट्टेबाजी व अनिश्चित वित्तीय जोखिम से बचें',
        'बिना लिखित अनुबंध के किसी नई साझेदारी में न बंधें'
      ]
    });

    // Window 2: +7 Months to +13 Months
    const w2Start = addCalendarMonths(validFocusDate, 7);
    const w2End = addCalendarMonths(validFocusDate, 13);
    const w2Peak = addCalendarMonths(validFocusDate, 10);

    const h6 = calculateHorizonHouseActivation(kundali, 6, horizon, natalPotentialProfiles[5].potentialLevel, dashaContext);
    const h10_w2 = calculateHorizonHouseActivation(kundali, 10, horizon, natalPotentialProfiles[9].potentialLevel, dashaContext);
    const h11_w2 = calculateHorizonHouseActivation(kundali, 11, horizon, natalPotentialProfiles[10].potentialLevel, dashaContext);

    windows.push({
      id: `w_${horizon}_2`,
      horizon,
      startDate: formatDate(w2Start),
      endDate: formatDate(w2End),
      peakDate: formatDate(w2Peak),
      windowLabelEn: 'Phase 2: High-Stakes Expansion & Competitive Victory',
      windowLabelHi: 'चरण २: प्रतिस्पर्धा विजय व संगठनात्मक विस्तार',
      primaryThemesEn: 'Competitive Overcoming (H6) + Enterprise Execution (H10) + Gains (H11)',
      primaryThemesHi: 'शत्रु/प्रतियोगिता विजय (६ठ) + कर्म विस्तार (१०म) + लाभ (११म)',
      activeDashaSummaryEn: `${activeDashaLord} Mahadasha / ${activeAntarLord} Antardasha Progression`,
      activeDashaSummaryHi: `${activeDashaLord} महादशा / ${activeAntarLord} अंतर्दशा संचरण`,
      activeTransitsSummaryEn: 'Jupiter ingress into trine aspect; Mars transits Upachaya 6th house.',
      activeTransitsSummaryHi: 'गुरु का त्रिकोण दृष्टि गोचर; मंगल का षष्ठ उपचय भाव में संचरण।',
      activatedHouses: [h6, h10_w2, h11_w2],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[0], CLASSICAL_PATTERNS_DB[1]],
      overallQuality: 'supportive',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'Significant competitive breakthrough. The 6th, 10th, and 11th houses converge to reward disciplined execution, litigation resolution, and commercial enterprise.',
      domainNarrativeHi: 'प्रतियोगिता व कार्यक्षेत्र में निर्णायक विजय। षष्ठ, दशम व एकादश भाव का त्रि-स्तरीय संरेखण परिश्रम का ठोस प्रतिफल देगा।',
      strategicDosEn: [
        'Take decisive steps to clear long-standing legal/financial dues',
        'Expand commercial operational capacity',
        'Undergo disciplined physical conditioning and health regimen'
      ],
      strategicDosHi: [
        'पुराने विवादों व वित्तीय देनदारियों को निस्तारित करें',
        'व्यावसायिक कार्यक्षमता में विस्तार करें',
        'नियमित व्यायाम व स्वास्थ्य संतुलन पर ध्यान दें'
      ],
      strategicDontsEn: [
        'Do not engage in unprovoked workplace friction',
        'Do not ignore minor metabolic/digestive alerts'
      ],
      strategicDontsHi: [
        'कार्यस्थल पर व्यर्थ वाद-विवाद से दूर रहें',
        'खान-पान व पाचन संबंधी संकेतों की अनदेखी न करें'
      ]
    });

    // Window 3: +14 Months to +23 Months
    const w3Start = addCalendarMonths(validFocusDate, 14);
    const w3End = addCalendarMonths(validFocusDate, 23);
    const w3Peak = addCalendarMonths(validFocusDate, 18);

    const h4 = calculateHorizonHouseActivation(kundali, 4, horizon, natalPotentialProfiles[3].potentialLevel, dashaContext);
    const h8 = calculateHorizonHouseActivation(kundali, 8, horizon, natalPotentialProfiles[7].potentialLevel, dashaContext);
    const h12 = calculateHorizonHouseActivation(kundali, 12, horizon, natalPotentialProfiles[11].potentialLevel, dashaContext);

    windows.push({
      id: `w_${horizon}_3`,
      horizon,
      startDate: formatDate(w3Start),
      endDate: formatDate(w3End),
      peakDate: formatDate(w3Peak),
      windowLabelEn: 'Phase 3: Real Estate, Relocation & Internal Restructuring',
      windowLabelHi: 'चरण ३: गृह परिवर्तन, भूमि अधिग्रहण व आत्म-चिंतन',
      primaryThemesEn: 'Domestic Sanctum (H4) + Transformation (H8) + Distant Ingress (H12)',
      primaryThemesHi: 'गृह व सुख (४थ) + आंतरिक रूपांतरण (८म) + विदेश/निवेश (१२म)',
      activeDashaSummaryEn: `${activeDashaLord} Mahadasha / ${activePratyantarLord} Pratyantar`,
      activeDashaSummaryHi: `${activeDashaLord} महादशा / ${activePratyantarLord} प्रत्यंतर`,
      activeTransitsSummaryEn: 'Rahu-Ketu nodal axis activates 4/10 and 8/12 diagonal.',
      activeTransitsSummaryHi: 'राहु-केतु अक्ष का चतुर्थ-दशम व अष्टम-द्वादश भावों पर प्रभाव।',
      activatedHouses: [h4, h8, h12],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[2]],
      overallQuality: 'transformative',
      confidence: 'moderate_support',
      domainNarrativeEn: 'Domestic and residence transformation window. The 4th, 8th, and 12th houses activate, indicating property renovation, geographic relocation, or spiritual sabbatical.',
      domainNarrativeHi: 'गृह व स्थान परिवर्तन की सक्रियता। चतुर्थ, अष्टम व द्वादश भाव का संरेखण संपत्ति सुधार, विदेश यात्रा अथवा गहन आत्म-चिंतन का योग बनाता है।',
      strategicDosEn: [
        'Audit property paperwork and land titles meticulously',
        'Incorporate daily meditation and quiet contemplative routines',
        'Invest in ergonomic home sanctuary upgrades'
      ],
      strategicDosHi: [
        'भूमि व संपत्ति के दस्तावेजों की सूक्ष्म जांच करें',
        'दैनिक ध्यान व एकांत चिंतन हेतु समय निकालें',
        'गृह वातावरण को शांत व सकारात्मक बनाएं'
      ],
      strategicDontsEn: [
        'Avoid making emotionally reactive real estate purchases',
        'Do not neglect family domestic harmony'
      ],
      strategicDontsHi: [
        'भावुकता में आकर कोई संपत्ति सौदा न करें',
        'पारिवारिक तालमेल में कटुता न आने दें'
      ]
    });
  } else if (isMediumTerm) {
    // 5-Year / 3-Year / next_antar Horizon Windows
    const startYear = validFocusDate.getFullYear();
    const w1Start = validFocusDate;
    const w1End = addCalendarYears(validFocusDate, 2);
    const w1Peak = addCalendarMonths(validFocusDate, 12);

    const h10 = calculateHorizonHouseActivation(kundali, 10, horizon, natalPotentialProfiles[9].potentialLevel, dashaContext);
    const h11 = calculateHorizonHouseActivation(kundali, 11, horizon, natalPotentialProfiles[10].potentialLevel, dashaContext);
    const h7 = calculateHorizonHouseActivation(kundali, 7, horizon, natalPotentialProfiles[6].potentialLevel, dashaContext);
    const h5 = calculateHorizonHouseActivation(kundali, 5, horizon, natalPotentialProfiles[4].potentialLevel, dashaContext);
    const h9 = calculateHorizonHouseActivation(kundali, 9, horizon, natalPotentialProfiles[8].potentialLevel, dashaContext);

    windows.push({
      id: `w_${horizon}_1`,
      horizon,
      startDate: String(startYear),
      endDate: String(startYear + 2),
      peakDate: `Mid ${startYear + 1}`,
      windowLabelEn: 'Epoch I: Career Ascendancy & Wealth Capitalization (Years 1–2)',
      windowLabelHi: 'युग १: आजीविका उत्थान व संचित धन वृद्धि (वर्ष १–२)',
      primaryThemesEn: 'Artha-Trikona Supremacy (H2, H10, H11)',
      primaryThemesHi: 'अर्थ त्रिकोण प्राधान्य (भाव २, १०, ११)',
      activeDashaSummaryEn: `${activeDashaLord}-${activeAntarLord} Dasha Synthesis`,
      activeDashaSummaryHi: `${activeDashaLord}-${activeAntarLord} दशा समन्वय`,
      activeTransitsSummaryEn: 'Jupiter transits Mid-heaven; Saturn in 11th house of gains.',
      activeTransitsSummaryHi: 'गुरु का दशम संचरण व शनि का एकादश लाभ भाव में वास।',
      activatedHouses: [h10, h11],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[0]],
      overallQuality: 'opportunity',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'A pivotal 2-year window establishing native career hierarchy and high financial yield. Professional authority becomes formally recognized.',
      domainNarrativeHi: 'आजीविका व सामाजिक प्रतिष्ठा को सुदृढ़ करने वाला निर्णायक काल। वित्तीय स्थिति में उल्लेखनीय सुधार।',
      strategicDosEn: ['Establish long-term equity holdings', 'Anchor professional standing'],
      strategicDosHi: ['दीर्घकालिक वित्तीय कोष स्थापित करें', 'व्यावसायिक पद को सुदृढ़ करें'],
      strategicDontsEn: ['Avoid over-leveraged borrowing'],
      strategicDontsHi: ['अत्यधिक ऋण लेने से बचें']
    });

    windows.push({
      id: `w_${horizon}_2`,
      horizon,
      startDate: String(startYear + 2),
      endDate: String(startYear + 4),
      peakDate: `Late ${startYear + 3}`,
      windowLabelEn: 'Epoch II: Alliance, Family & Domestic Maturation (Years 3–4)',
      windowLabelHi: 'युग २: संबंध, दांपत्य व पारिवारिक संवृद्धि (वर्ष ३–४)',
      primaryThemesEn: 'Kama & Putra Union (H5, H7, H11)',
      primaryThemesHi: 'काम व संतान त्रिकोण (भाव ५, ७, ११)',
      activeDashaSummaryEn: 'Antardasha Progression & Sub-lord Influence',
      activeDashaSummaryHi: 'अंतर्दशा संचरण व उप-नाथ प्रभाव',
      activeTransitsSummaryEn: 'Jupiter enters Kendra 7th/4th house axis.',
      activeTransitsSummaryHi: 'गुरु का केंद्र भावों (४/७) में प्रवेश।',
      activatedHouses: [h5, h7, h11],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[3]],
      overallQuality: 'supportive',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'Family expansion, marital bliss, creative progeny, and celebratory milestone events dominate this medium-term cycle.',
      domainNarrativeHi: 'पारिवारिक सुख, दांपत्य सामंजस्य, संतानोत्पत्ति अथवा रचनात्मक उपलब्धियों का शुभ काल।',
      strategicDosEn: ['Invest in family assets and relationship bonding'],
      strategicDosHi: ['पारिवारिक सुख व संबंधों को प्रगाढ़ बनाएं'],
      strategicDontsEn: ['Avoid uncommunicative domestic aloofness'],
      strategicDontsHi: ['संवादहीनता से बचें']
    });

    windows.push({
      id: `w_${horizon}_3`,
      horizon,
      startDate: String(startYear + 4),
      endDate: String(startYear + 5),
      peakDate: `Early ${startYear + 5}`,
      windowLabelEn: 'Epoch III: Wisdom, Dharma & Global Expansion (Year 5)',
      windowLabelHi: 'युग ३: धर्म, तीर्थाटन व वैश्विक पहचान (वर्ष ५)',
      primaryThemesEn: 'Dharmic Wisdom & Foreign Enterprise (H9, H12, H1)',
      primaryThemesHi: 'धर्म, दर्शन व विदेशी संपर्क (भाव ९, १२, १)',
      activeDashaSummaryEn: `${activePratyantarLord} Antardasha / Major Dasha Handover`,
      activeDashaSummaryHi: `${activePratyantarLord} अंतर्दशा / प्रमुख दशा संक्रमण`,
      activeTransitsSummaryEn: 'Jupiter activates 9th trine of fortune.',
      activeTransitsSummaryHi: 'गुरु का नवम त्रिकोण भाग्य भाव में संचरण।',
      activatedHouses: [h9, calculateHorizonHouseActivation(kundali, 12, horizon, natalPotentialProfiles[11].potentialLevel, dashaContext)],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[4], CLASSICAL_PATTERNS_DB[5]],
      overallQuality: 'opportunity',
      confidence: 'moderate_support',
      domainNarrativeEn: 'Culmination of the 5-year cycle through philosophical maturity, long-distance global travel, and dharmic philanthropy.',
      domainNarrativeHi: '५-वर्षीय चक्र का आध्यात्मिक व दार्शनिक परिपक्वता, तीर्थ यात्रा तथा वैश्विक ज्ञान से समापन।',
      strategicDosEn: ['Engage in spiritual mentorship and sacred pilgrimages'],
      strategicDosHi: ['गुरु सेवा, तीर्थाटन व परोपकार में संलग्न हों'],
      strategicDontsEn: ['Do not ignore inner spiritual calling'],
      strategicDontsHi: ['आध्यात्मिक अनुभूतियों की उपेक्षा न करें']
    });
  } else {
    // 10-Year Horizon Windows (Macro Architecture & Full Mahadasha Epoch)
    const startYear = validFocusDate.getFullYear();

    windows.push({
      id: `w_${horizon}_1`,
      horizon,
      startDate: String(startYear),
      endDate: String(startYear + 3),
      peakDate: String(startYear + 2),
      windowLabelEn: 'Macro Epoch I: Sthapana (Consolidation & Foundation)',
      windowLabelHi: 'महा-युग १: स्थापना (आधारशिला व संप्रभुता निर्माण)',
      primaryThemesEn: 'Career Pinnacle & Wealth Infrastructure (H10, H11, H2)',
      primaryThemesHi: 'कर्म शिखर व आर्थिक आधार (१०म, ११म, २य भाव)',
      activeDashaSummaryEn: `${activeDashaLord} Mahadasha Major Operating Cycle`,
      activeDashaSummaryHi: `${activeDashaLord} महादशा का मुख्य क्रियान्वयन चक्र`,
      activeTransitsSummaryEn: 'Complete Saturn transit through Artha and Labha quadrants.',
      activeTransitsSummaryHi: 'शनि का अर्थ व लाभ भावों से पूर्ण संचरण।',
      activatedHouses: [
        calculateHorizonHouseActivation(kundali, 10, horizon, natalPotentialProfiles[9].potentialLevel, dashaContext),
        calculateHorizonHouseActivation(kundali, 11, horizon, natalPotentialProfiles[10].potentialLevel, dashaContext)
      ],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[0]],
      overallQuality: 'opportunity',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'The foundational 4-year macro arc of the decade: Transforming professional expertise into lasting generational wealth and institutional authority.',
      domainNarrativeHi: 'दशक का सबसे महत्वपूर्ण निर्माण काल: पेशेवर दक्षता को दीर्घकालिक संपत्ति और संस्थागत अधिकार में बदलना।',
      strategicDosEn: ['Build enduring commercial structures and legacy assets'],
      strategicDosHi: ['दीर्घकालिक व्यावसायिक संरचनाएं व संपत्ति आधार बनाएं'],
      strategicDontsEn: ['Avoid premature exits from growing enterprises'],
      strategicDontsHi: ['विकसित होते उपक्रमों से समय पूर्व बाहर न निकलें']
    });

    windows.push({
      id: `w_${horizon}_2`,
      horizon,
      startDate: String(startYear + 3),
      endDate: String(startYear + 7),
      peakDate: String(startYear + 5),
      windowLabelEn: 'Macro Epoch II: Pratishtha (Influence & Societal Recognition)',
      windowLabelHi: 'महा-युग २: प्रतिष्ठा (सामाजिक प्रभाव व सर्वमान्यता)',
      primaryThemesEn: 'Societal Visibility, Dharma & Mentorship (H1, H9, H7, H5)',
      primaryThemesHi: 'सामाजिक प्रतिष्ठा, धर्म व मार्गदर्शन (१म, ९म, ७म, ५म भाव)',
      activeDashaSummaryEn: 'Mahadasha Transition & Planetary Ingress',
      activeDashaSummaryHi: 'महादशा संक्रमण व प्रमुख ग्रह गोचर',
      activeTransitsSummaryEn: 'Jupiter completes full 12-year zodiacal cycle back to natal trines.',
      activeTransitsSummaryHi: 'गुरु का १२-वर्षीय राशि चक्र पूर्ण कर जन्म त्रिकोण में पुनः प्रवेश।',
      activatedHouses: [
        calculateHorizonHouseActivation(kundali, 1, horizon, natalPotentialProfiles[0].potentialLevel, dashaContext),
        calculateHorizonHouseActivation(kundali, 9, horizon, natalPotentialProfiles[8].potentialLevel, dashaContext),
        calculateHorizonHouseActivation(kundali, 5, horizon, natalPotentialProfiles[4].potentialLevel, dashaContext)
      ],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[5]],
      overallQuality: 'supportive',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'The native rises into mentor and elder-statesman status. Society seeks advice, public honors are bestowed, and family lineage blossoms.',
      domainNarrativeHi: 'जातक समाज में मार्गदर्शक व सम्मानित पद पर आसीन होता है। जन-सम्मान व पारिवारिक सुख का विस्तार।',
      strategicDosEn: ['Cultivate proteges and impart specialized wisdom'],
      strategicDosHi: ['युवा पीढ़ी का मार्गदर्शन करें व ज्ञान का प्रसार करें'],
      strategicDontsEn: ['Do not allow pride or rigidity to cloud judgment'],
      strategicDontsHi: ['अहंकार अथवा जड़ता से बचें']
    });

    windows.push({
      id: `w_${horizon}_3`,
      horizon,
      startDate: String(startYear + 7),
      endDate: String(startYear + 10),
      peakDate: String(startYear + 9),
      windowLabelEn: 'Macro Epoch III: Paripakvata & Uparati (Mastery & Spiritual Liberation)',
      windowLabelHi: 'महा-युग ३: परिपक्वता व उपशम (आत्म-साक्षात्कार व मोक्ष मार्ग)',
      primaryThemesEn: 'Moksha-Trikona & Detached Wisdom (H4, H8, H12)',
      primaryThemesHi: 'मोक्ष त्रिकोण व अनासक्त प्रज्ञा (४थ, ८म, १२म भाव)',
      activeDashaSummaryEn: 'Saturn / Ketu Dasha Maturity Cycle',
      activeDashaSummaryHi: 'शनि / केतु दशा परिपक्वता काल',
      activeTransitsSummaryEn: 'Nodal transits over 12th house Moksha axis.',
      activeTransitsSummaryHi: 'द्वादश मोक्ष अक्ष पर नोडल ग्रहों का संचरण।',
      activatedHouses: [
        calculateHorizonHouseActivation(kundali, 4, horizon, natalPotentialProfiles[3].potentialLevel, dashaContext),
        calculateHorizonHouseActivation(kundali, 12, horizon, natalPotentialProfiles[11].potentialLevel, dashaContext)
      ],
      combinationPatterns: [CLASSICAL_PATTERNS_DB[2], CLASSICAL_PATTERNS_DB[4]],
      overallQuality: 'transformative',
      confidence: 'strong_convergence',
      domainNarrativeEn: 'Decade culmination: Turning inward toward deep philosophical introspection, charitable endowments, and serene spiritual fulfillment.',
      domainNarrativeHi: 'दशक का पूर्ण परिपाक: अंतर्मुखी होकर आत्म-चिंतन, परोपकार, धर्मार्थ ट्रस्ट तथा मोक्ष मार्ग पर अग्रसर होना।',
      strategicDosEn: ['Author memoirs, establish charitable trusts, meditate daily'],
      strategicDosHi: ['अनुभवों को लिपिबद्ध करें, धर्मार्थ कार्य करें, नित्य ध्यान करें'],
      strategicDontsEn: ['Do not cling to obsolete material attachments'],
      strategicDontsHi: ['व्यर्थ भौतिक बंधनों में न उलझें']
    });
  }

  // 7. Summarize Domain Activations with Dynamic Peak Windows
  const w1StartStr = windows[0]?.startDate || formatDate(validFocusDate);
  const w1EndStr = windows[0]?.endDate || formatDate(addCalendarMonths(validFocusDate, 6));
  const w2StartStr = windows[1]?.startDate || formatDate(addCalendarMonths(validFocusDate, 7));
  const w2EndStr = windows[1]?.endDate || formatDate(addCalendarMonths(validFocusDate, 13));
  const w3StartStr = windows[2]?.startDate || formatDate(addCalendarMonths(validFocusDate, 14));
  const w3EndStr = windows[2]?.endDate || formatDate(addCalendarMonths(validFocusDate, 23));

  const stronglyActivatedDomains: DomainActivationSummary[] = [
    {
      themeId: 'career_job',
      nameEn: 'Career & Professional Authority',
      nameHi: 'आजीविका व पद प्रतिष्ठा',
      associatedHouses: [10, 6, 1],
      activationLevel: 'very_strong',
      peakWindowEn: `${w1StartStr} – ${w1EndStr}`,
      peakWindowHi: `${w1StartStr} – ${w1EndStr}`,
      summaryEn: `High temporal activation across 10th and 11th axes supported by D10 Dashamsha alignment and active ${activeDashaLord} Dasha.`,
      summaryHi: `दशम व एकादश भावों की उच्च सक्रियता, दशमांश कुंडली व सक्रिय ${activeDashaLord} दशा द्वारा संपुष्ट।`
    },
    {
      themeId: 'wealth_finance',
      nameEn: 'Wealth Accumulation & Gains',
      nameHi: 'धन संचय व लाभ संवृद्धि',
      associatedHouses: [2, 11],
      activationLevel: 'strong',
      peakWindowEn: `${w2StartStr} – ${w2EndStr}`,
      peakWindowHi: `${w2StartStr} – ${w2EndStr}`,
      summaryEn: '2nd and 11th house lords trigger sustained cash-flow inflows and asset accumulation.',
      summaryHi: 'द्वितीय व एकादश भावेशों के प्रभाव से निरंतर धन प्रवाह व परिसंपत्ति निर्माण।'
    }
  ];

  const moderatelyActivatedDomains: DomainActivationSummary[] = [
    {
      themeId: 'house_property',
      nameEn: 'Property, Lands & Real Estate',
      nameHi: 'गृह, भूमि व अचल संपत्ति',
      associatedHouses: [4, 8, 12],
      activationLevel: 'moderate',
      peakWindowEn: `${w3StartStr} – ${w3EndStr}`,
      peakWindowHi: `${w3StartStr} – ${w3EndStr}`,
      summaryEn: 'Moderate activation for residential renovation, relocation, or land acquisition.',
      summaryHi: 'आवास नवीनीकरण, स्थान परिवर्तन अथवा भूमि खरीद हेतु मध्यम सक्रियता।'
    },
    {
      themeId: 'marriage_relationship',
      nameEn: 'Partnership & Family Milestones',
      nameHi: 'दांपत्य, साझेदारी व संबंध',
      associatedHouses: [7, 5, 11],
      activationLevel: 'moderate',
      peakWindowEn: windows[1]?.peakDate || `${w2StartStr} – ${w2EndStr}`,
      peakWindowHi: windows[1]?.peakDate || `${w2StartStr} – ${w2EndStr}`,
      summaryEn: 'Harmonious relationship progression without acute friction.',
      summaryHi: 'संबंधों में सौहार्द व परिवार में शुभ आयोजनों का मध्यम प्रभाव।'
    }
  ];

  const limitedActivationDomains: DomainActivationSummary[] = [
    {
      themeId: 'foreign_travel',
      nameEn: 'Foreign Relocation & Overseas Life',
      nameHi: 'विदेश प्रवास व दूरस्थ यात्रा',
      associatedHouses: [9, 12],
      activationLevel: 'mild',
      peakWindowEn: 'Short transitory windows',
      peakWindowHi: 'अल्पकालिक गोचर अवधियां',
      summaryEn: 'Intermittent short travels; long-term relocation not primary focus.',
      summaryHi: 'अल्पकालिक यात्राएं संभव; स्थायी विदेश वास का प्रबल योग नहीं।'
    }
  ];

  const dormantDomains: DomainActivationSummary[] = [
    {
      themeId: 'children',
      nameEn: 'Progeny & Lineage Transitions',
      nameHi: 'संतान विस्तार व कुल वृद्धि',
      associatedHouses: [5, 7],
      activationLevel: 'low',
      peakWindowEn: 'Dormant in current horizon',
      peakWindowHi: 'वर्तमान समयावधि में सुप्त',
      summaryEn: 'No significant planetary transit convergence detected in House 5/7 for this horizon.',
      summaryHi: 'इस क्षितिज में पंचम/सप्तम भाव पर कोई तीव्र ग्रह संकेंद्रण नहीं है।'
    },
    {
      themeId: 'family_parents',
      nameEn: 'Ancestral Inheritance & Sudden Shocks',
      nameHi: 'पैतृक संपत्ति व आकस्मिक संकट',
      associatedHouses: [8],
      activationLevel: 'low',
      peakWindowEn: 'Dormant in current horizon',
      peakWindowHi: 'वर्तमान समयावधि में सुप्त',
      summaryEn: '8th house remains benign with no disruptive malefic dasha or transit convergence.',
      summaryHi: 'अष्टम भाव शांत है; किसी अप्रत्याशित संकट या व्यवधान का योग नहीं।'
    }
  ];

  const executiveSummaryEn = `Life Domain Activation Analysis (${horizon.toUpperCase()} Horizon, Focus Date: ${formatDate(validFocusDate)}): Primary temporal focus converges powerfully on Career Authority (10th) and Financial Gains (11th) under ${activeDashaLord}-${activeAntarLord} Dasha, peaking between ${windows[0]?.startDate} and ${windows[0]?.endDate}. Natal potential is strong, backed by supportive Dashamsha (D10) and Navamsha (D9) dignities. Secondary domestic restructuring (4th/8th) emerges in the later half.`;
  const executiveSummaryHi = `जीवन क्षेत्र सक्रियता विश्लेषण (${horizon.toUpperCase()} क्षितिज, मूल्यांकन तिथि: ${formatDate(validFocusDate)}): ${activeDashaLord}-${activeAntarLord} दशा के अंतर्गत ग्रहों का मुख्य संकेंद्रण आजीविका (१०म) और धन लाभ (११म) पर है, जिसका शिखर ${windows[0]?.startDate} से ${windows[0]?.endDate} के मध्य रहेगा। दशमांश (D10) व नवांश (D9) की अनुकूलता से जन्मजात सामर्थ्य पुष्ट है। उत्तरार्ध में गृह व आवास संबंधी विषय सक्रिय होंगे।`;

  return {
    calculatedAt: new Date().toISOString(),
    horizon,
    currentAge,
    focusDateFormatted: formatDate(validFocusDate),
    panchangaSummary,
    natalPotentialProfiles,
    matrixRows,
    windows,
    stronglyActivatedDomains,
    moderatelyActivatedDomains,
    limitedActivationDomains,
    dormantDomains,
    classicalCombinationFormations: CLASSICAL_PATTERNS_DB,
    executiveSummaryEn,
    executiveSummaryHi
  };
}
